import type { ChatDocument, ChatMessageDocument } from '@models/chat';
import type { OfferDocument } from '@models/offer';
import { useQuery } from '@tanstack/react-query';
import { getPublicUserDoc, getUserById } from '@util/firestore/users';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useState } from 'react';
import MessageInbound from '@c/chat/MessageInbound';
import MessageOutbound from '@c/chat/MessageOutbound';
import MessageAuto from '@c/chat/MessageAuto';
import MessageOffer from '@c/chat/MessageOffer';
import MessageMenu from '@c/chat/MessageMenu';

interface MessageBubbleProps {
  chat: ChatDocument;
  realtimeMessages?: ChatMessageDocument[];
  chatMessage: ChatMessageDocument;
  fallbackImg?: string;
  onImageClick: (image: string) => void;
  offer?: OfferDocument | null;
  isSidebar?: boolean;
}

export default function MessageBubble({
  chat,
  realtimeMessages,
  chatMessage,
  fallbackImg,
  onImageClick,
  offer,
  isSidebar,
}: MessageBubbleProps) {
  const { userDoc } = useAuth();
  const { showSuccessToast } = useToastContext();

  const { data: messageUser } = useQuery({
    queryKey: ['user', chatMessage.uid],
    queryFn: () => getUserById(chatMessage.uid),
    enabled: !!chatMessage.uid,
  });

  // Use the offer prop passed from parent component
  const realtimeOffer: OfferDocument | null = offer ?? null;
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  if (!userDoc) return null;

  const onRightClick = (ev: React.MouseEvent) => {
    if (userDoc?.roles?.support) {
      ev.preventDefault();
      ev.stopPropagation();
      setMenuPosition({ x: ev.clientX, y: ev.clientY });
      setMenuOpen(true);
    }
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(chatMessage.content);
    showSuccessToast('Message copied to clipboard', 'bottom-center');
  };

  const isOutbound = chatMessage.uid === userDoc?.uid;
  const isAuto = chatMessage.is_auto;
  const hideMessage =
    isAuto &&
    chatMessage.auto_recipient &&
    chatMessage.auto_recipient !== userDoc.uid &&
    !userDoc.roles?.support;
  const isOfferMessage = chatMessage.content.startsWith('New Offer:');
  const isExclusiveOfferMessage = chatMessage.content.startsWith(
    'A seller sent you an exclusive offer'
  );

  if (
    chatMessage.content.startsWith(
      'The seller sent you and other interested buyers'
    ) &&
    chat.is_offer &&
    chat.seller_id === userDoc?.uid
  )
    return null;

  const offerMessages = realtimeMessages?.filter(
    (m) =>
      m.offer_amount ||
      m.content.startsWith('New Offer: ') ||
      m.content.startsWith('A seller sent you an exclusive offer')
  );
  const currentOfferMessageIndex = offerMessages?.findIndex(
    (m) => m.created_at === chatMessage.created_at
  );

  // Handle offer messages
  if (isOfferMessage || isExclusiveOfferMessage) {
    return (
      <MessageOffer
        message={chatMessage}
        messageUser={messageUser}
        offer={realtimeOffer}
        isOldOffer={
          (offerMessages?.length ?? 0) - 1 !== currentOfferMessageIndex
        }
        isOutbound={isOutbound}
      />
    );
  }

  return (
    <div className="grid">
      {isAuto ? (
        !hideMessage && (
          <MessageAuto message={chatMessage} onRightClick={onRightClick} />
        )
      ) : isOutbound ? (
        <MessageOutbound
          message={chatMessage}
          timestamp={chatMessage.created_at ?? 0}
          fallbackImg={fallbackImg}
          user={messageUser}
          isSupport={userDoc.roles?.support}
          onImageClick={onImageClick}
          onRightClick={onRightClick}
          isSidebar={isSidebar}
        />
      ) : (
        <MessageInbound
          message={chatMessage}
          timestamp={chatMessage.created_at ?? 0}
          isSupport={messageUser?.roles?.support}
          user={messageUser}
          fallbackImg={fallbackImg}
          onImageClick={onImageClick}
          onRightClick={onRightClick}
          isSidebar={isSidebar}
        />
      )}

      <MessageMenu
        userDoc={userDoc}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        position={menuPosition}
        chat={chat}
        message={chatMessage}
        isOutbound={isOutbound}
        onCopyMessage={handleCopyMessage}
      />
    </div>
  );
}
